// A tab that booted an earlier build can ask for a chunk the server no longer
// has (retention expired, rollback). The import then fails; the user is told
// the app was updated and offered one reload. Nothing reloads by itself, so an
// unsaved Preview is never discarded behind the user's back and a reload loop
// cannot start.

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite preload helper
  /'text\/html' is not a valid JavaScript MIME type/i,
];

export function isChunkLoadError(error: unknown): boolean {
  // Offline, the same import error means "no network", not "new build".
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

let stale = false;
/** Views already showing their own reload prompt (DeferredView). */
let inlinePrompts = 0;
const listeners = new Set<() => void>();

export function isStaleBuild(): boolean {
  return stale;
}

/** The global notice is only needed when no view on screen already offers the reload. */
export function needsStaleBuildNotice(): boolean {
  return stale && inlinePrompts === 0;
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** Returns the release function. */
export function holdInlineStalePrompt(): () => void {
  inlinePrompts += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    inlinePrompts -= 1;
    emit();
  };
}

export function subscribeStaleBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function noteChunkLoadFailure(error: unknown): void {
  console.warn("[ato:stale-build] chunk failed to load", error instanceof Error ? error.message : error);
  if (stale) return;
  stale = true;
  emit();
}

let installed = false;

/** Vite dispatches `vite:preloadError` when a dynamic import's chunk or CSS fails. */
export function installChunkLoadRecovery(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("vite:preloadError", (event) => {
    if (navigator.onLine === false) return;
    noteChunkLoadFailure((event as Event & { payload?: unknown }).payload);
  });
}

/** Test seam. */
export function resetStaleBuildForTests(): void {
  stale = false;
  inlinePrompts = 0;
  listeners.clear();
}
